import { useCallback, useMemo, useState } from "react";

interface SaveStatusState {
  /** False until the first save this visit, so a fresh surface shows no status. */
  readonly touched: boolean;
  readonly isSaving: boolean;
  readonly lastSavedIso: string | null;
  readonly hasError: boolean;
}

const INITIAL: SaveStatusState = {
  touched: false,
  isSaving: false,
  lastSavedIso: null,
  hasError: false,
};

/**
 * The outcome of a click- or pick-to-save write, for an `AutosaveStatusPill`
 * beside it (NG-2803): Saving… while it runs, a brief Saved, a persistent Save
 * failed, and at rest (with `hideAtRest`) a screen-reader "Up to date" that only
 * a confirmed write produces. A toast alone auto-dismisses, so neither a user
 * nor a spec can tell from it whether the value was stored.
 *
 * Call `begin` when the write starts, then `succeed` or `fail`. Render the pill
 * only while `touched`, spreading `pillProps`:
 *
 *     {save.touched ? <AutosaveStatusPill {...save.pillProps} hideAtRest /> : null}
 */
export function useSaveStatus() {
  const [state, setState] = useState<SaveStatusState>(INITIAL);
  const begin = useCallback(
    () => setState((prev) => ({ ...prev, touched: true, isSaving: true, hasError: false })),
    [],
  );
  const succeed = useCallback(
    () =>
      setState((prev) => ({ ...prev, isSaving: false, lastSavedIso: new Date().toISOString() })),
    [],
  );
  const fail = useCallback(
    () => setState((prev) => ({ ...prev, isSaving: false, hasError: true })),
    [],
  );
  const pillProps = useMemo(
    () => ({
      isSaving: state.isSaving,
      lastSavedIso: state.lastSavedIso,
      hasError: state.hasError,
      hasUnpublishedChanges: false,
      changedFields: [] as string[],
    }),
    [state.isSaving, state.lastSavedIso, state.hasError],
  );
  return { touched: state.touched, isSaving: state.isSaving, pillProps, begin, succeed, fail };
}
